import {
  Component,
  Inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import html2canvas from 'html2canvas-pro';

import { FeedbackReportService } from '../../services/feedback-report.service';
import { ToastService } from '../../services/general/toast.service';
import { FeedbackReportMetadata } from '../../models/feedback-report.model';

export interface ModalFeedbackReportData {
  entityId?: string | number;
}

@Component({
  selector: 'app-modal-feedback-report',
  standalone: true,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './modal-feedback-report.component.html',
  styleUrls: ['./modal-feedback-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalFeedbackReportComponent {
  commento = '';
  includeScreenshot = true;

  readonly submitting = signal(false);
  readonly isSubmitting = computed(() => this.submitting());

  constructor(
    public dialogRef: MatDialogRef<ModalFeedbackReportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModalFeedbackReportData,
    private feedbackService: FeedbackReportService,
    private toast: ToastService
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  /**
   * html2canvas non supporta oklab/oklch (usati da Angular Material e CSS moderno).
   * Sostituiamo con rgb equivalente prima che html2canvas parsifichi il CSS.
   */
  private replaceOklabColors(doc: Document): void {
    const rgbFallback = 'rgb(128, 128, 128)';
    const replaceOklab = (text: string) =>
      text.replace(/oklab\([^)]*\)/g, rgbFallback).replace(/oklch\([^)]*\)/g, rgbFallback);

    doc.querySelectorAll('style').forEach((styleEl) => {
      if (styleEl.textContent?.includes('oklab') || styleEl.textContent?.includes('oklch')) {
        styleEl.textContent = replaceOklab(styleEl.textContent);
      }
    });
    doc.querySelectorAll('[style]').forEach((el) => {
      const style = el.getAttribute('style');
      if (style?.includes('oklab') || style?.includes('oklch')) {
        el.setAttribute('style', replaceOklab(style));
      }
    });
    // I fogli esterni (es. Angular Material) possono contenere oklab: rimuoviamoli
    // per evitare che html2canvas li parsifichi. Lo screenshot avrà meno stile ma funzionerà.
    doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
  }

  async submit(): Promise<void> {
    const commentoTrimmed = this.commento?.trim();
    if (!commentoTrimmed) {
      this.toast.warn('Segnalazione', 'Inserisci un commento.');
      return;
    }

    this.submitting.set(true);

    let screenshot: Blob | null = null;

    if (this.includeScreenshot) {
      try {
        const target = (document.querySelector('.content') as HTMLElement) ?? document.body;
        const canvas = await html2canvas(target, {
          scale: 1,
          useCORS: true,
          logging: false,
          allowTaint: true,
          ignoreElements: (el) => el.tagName === 'IMG',
          onclone: (clonedDoc) => {
            this.replaceOklabColors(clonedDoc);
          },
        });
        screenshot = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
            'image/png',
            0.9
          );
        });
      } catch (err) {
        console.warn('Screenshot capture failed:', err);
        this.toast.warn(
          'Screenshot',
          'Impossibile catturare lo screenshot. La segnalazione verrà inviata senza.'
        );
      }
    }

    const metadata = this.feedbackService.collectMetadata(this.data?.entityId);

    this.feedbackService
      .submitSegnalazione(commentoTrimmed, screenshot, metadata)
      .subscribe({
        next: (result) => {
          this.submitting.set(false);
          if (result != null) {
            this.toast.success(
              'Segnalazione inviata',
              'Grazie per il tuo feedback. Lo esamineremo al più presto.'
            );
            this.dialogRef.close(true);
          }
        },
        error: () => {
          this.submitting.set(false);
        },
      });
  }
}
